import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  FiChevronLeft, 
  FiArrowRight, 
  FiFileText, 
  FiShield, 
  FiUserCheck, 
  FiCheckCircle, 
  FiBookOpen
} from 'react-icons/fi';

const TermsAndConditionsPage = () => {
  const [accepted, setAccepted] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [100, 0, 0, 100]);
  
  // Referencias a las secciones para el scroll
  const sectionRefs = {
    section1: useRef(null),
    section2: useRef(null),
    section3: useRef(null),
    section4: useRef(null),
    section5: useRef(null)
  };
  
  // Para comprobar si el usuario ya ha aceptado los términos
  useEffect(() => {
    const hasAccepted = localStorage.getItem('avlink_terms_accepted');
    if (hasAccepted) {
      setAccepted(true);
    }
    
    // Scroll al inicio cuando se carga el componente
    window.scrollTo(0, 0);
    
    // Configurar intersection observer para detectar secciones activas
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id');
            setActiveSection(id);
          }
        });
      },
      { rootMargin: '-20% 0px -75% 0px' }
    );
    
    // Observar todas las secciones
    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      Object.values(sectionRefs).forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);
  
  // Función para marcar los términos como aceptados
  const handleAcceptTerms = () => {
    localStorage.setItem('avlink_terms_accepted', 'true');
    setAccepted(true);
    // Opcional: redirigir a la página de inicio después de aceptar
    // navigate('/');
  };
  
  const goToHome = () => {
    navigate('/');
  };
  
  // Animación para las secciones al hacer scroll
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8 }
    }
  };
  
  const scrollTo = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Tabla de contenidos
  const tableOfContents = [
    { id: 'section-1', title: 'Introducción', icon: <FiFileText /> },
    { id: 'section-2', title: 'Definiciones', icon: <FiBookOpen /> },
    { id: 'section-3', title: 'Registro y Cuenta de Usuario', icon: <FiUserCheck /> },
    { id: 'section-4', title: 'Derechos y Restricciones de Uso', icon: <FiShield /> },
    { id: 'section-5', title: 'Contenido de la Plataforma', icon: <FiCheckCircle /> }
  ];

  return (
    <div className="pt-24 pb-20 bg-dark-900" ref={containerRef}>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 mb-16">
        <div className="absolute inset-0 opacity-5" 
             style={{
               backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px)',
               backgroundSize: '20px 20px'
             }}>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Términos y <span className="text-primary-500">Condiciones</span>
            </h1>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed">
              El presente documento establece las normas y condiciones para el uso de la plataforma
              Avlink Network, sus servicios y funcionalidades.
            </p>
            
            <div className="flex items-center justify-center text-sm text-gray-400">
              <div className="flex items-center">
                <FiFileText className="mr-2 text-primary-500" />
                <span>Última actualización: 10 de Mayo de 2025</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6 relative">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar con Tabla de Contenidos (sticky) */}
          <div className="md:w-1/4 lg:w-1/5">
            <div className="sticky top-24">
              <div className="bg-dark-800/80 backdrop-blur-sm rounded-xl border border-dark-600 p-5 mb-6">
                <h3 className="text-lg font-bold text-white mb-4">Contenido</h3>
                <nav>
                  <ul className="space-y-3">
                    {tableOfContents.map((item) => (
                      <li key={item.id}>
                        <button
                          onClick={() => scrollTo(item.id)}
                          className={`w-full flex items-center p-2 rounded-lg text-left transition-colors ${
                            activeSection === item.id
                              ? 'bg-primary-900/30 text-primary-400 border-l-2 border-primary-500 pl-3'
                              : 'text-gray-400 hover:text-white hover:bg-dark-700'
                          }`}
                        >
                          <span className="mr-3 text-primary-500">{item.icon}</span>
                          <span className="text-sm">{item.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
              
              <div className="hidden md:block">
                <Link
                  to="/"
                  className="w-full flex items-center justify-center py-3 px-4 mb-4 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  <FiChevronLeft className="mr-2" />
                  Volver al inicio
                </Link>
                
                {!accepted && (
                  <button
                    onClick={handleAcceptTerms}
                    disabled={!accepted}
                    className={`w-full flex items-center justify-center py-3 px-4 rounded-lg transition-colors text-sm font-medium bg-primary-600 hover:bg-primary-500 text-white`}
                  >
                    Aceptar Términos
                    <FiCheckCircle className="ml-2" />
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Contenido Principal */}
          <div className="md:w-3/4 lg:w-4/5">
            <div className="bg-dark-800/80 backdrop-blur-sm rounded-xl border border-dark-600 p-6 lg:p-8 mb-12">
              <div className="prose prose-invert prose-lg max-w-none">
                <div className="bg-dark-700/80 border-l-4 border-primary-500 p-4 mb-8">
                  <p className="font-medium text-white">IMPORTANTE:</p>
                  <p className="text-gray-300">Por favor, lea detenidamente estos Términos y Condiciones antes de utilizar la plataforma Avlink Network. Este documento establece los derechos, obligaciones y limitaciones que rigen el uso de nuestra plataforma.</p>
                </div>
                
                {/* Sección 1: Introducción */}
                <motion.section 
                  id="section-1" 
                  ref={sectionRefs.section1}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">1</span>
                    <span>INTRODUCCIÓN</span>
                  </h2>
                  <div className="space-y-4 text-gray-300">
                    <p>
                      Bienvenido a Avlink Network, una plataforma digital orientada a profesionales y empresas del rubro audiovisual (AV), con el objetivo de fomentar la capacitación, conectar talentos con oportunidades laborales y generar valor técnico en la comunidad mediante recursos educativos, eventos y networking.
                    </p>
                    <p>
                      Estos Términos y Condiciones ("Términos") constituyen un acuerdo legal entre usted y Avlink Network ("nosotros", "nuestro", "la plataforma") que rige su acceso y uso de la plataforma Avlink Network, incluyendo todos sus contenidos, funcionalidades y servicios disponibles.
                    </p>
                    <p>
                      Al registrarse, acceder o utilizar nuestra plataforma, usted acepta quedar vinculado por estos Términos. Si no está de acuerdo con estos Términos, por favor no acceda ni utilice nuestros servicios.
                    </p>
                  </div>
                </motion.section>
                
                {/* Sección 2: Definiciones */}
                <motion.section 
                  id="section-2" 
                  ref={sectionRefs.section2}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">2</span>
                    <span>DEFINICIONES</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Plataforma</h3>
                      <p className="text-gray-300 text-sm">Sitio web, aplicaciones y servicios de Avlink Network.</p>
                    </div>
                    
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Usuario</h3>
                      <p className="text-gray-300 text-sm">Cualquier persona que accede o utiliza la plataforma.</p>
                    </div>
                    
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Usuario Individual</h3>
                      <p className="text-gray-300 text-sm">Técnicos, estudiantes o formadores del sector audiovisual.</p>
                    </div>
                    
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Empresa</h3>
                      <p className="text-gray-300 text-sm">Proveedores de servicios, distribuidores o fabricantes del sector audiovisual.</p>
                    </div>
                    
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600 md:col-span-2">
                      <h3 className="font-medium text-white mb-2">Contenido</h3>
                      <p className="text-gray-300 text-sm">Cursos, documentos, videos, eventos, ofertas laborales y cualquier otro material disponible en la plataforma.</p>
                    </div>
                  </div>
                </motion.section>
                
                {/* Sección 3: Registro y Cuenta de Usuario */}
                <motion.section 
                  id="section-3" 
                  ref={sectionRefs.section3}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">3</span>
                    <span>REGISTRO Y CUENTA DE USUARIO</span>
                  </h2>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">3.1. Requisitos de registro</h3>
                  <p className="mb-4 text-gray-300">Para utilizar ciertos aspectos de nuestra plataforma, deberá registrarse y crear una cuenta. Usted acepta:</p>
                  <ul className="list-disc pl-5 space-y-2 mb-6 text-gray-300">
                    <li>Proporcionar información precisa, actualizada y completa durante el proceso de registro.</li>
                    <li>Mantener y actualizar su información para mantenerla precisa, actualizada y completa.</li>
                    <li>Ser responsable de mantener la confidencialidad de su contraseña.</li>
                    <li>Ser responsable de todas las actividades que ocurran bajo su cuenta.</li>
                    <li>Notificar inmediatamente a Avlink Network sobre cualquier uso no autorizado de su cuenta.</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">3.2. Tipos de cuenta</h3>
                  <p className="mb-4 text-gray-300">Avlink Network ofrece diferentes tipos de cuentas:</p>
                  <div className="grid grid-cols-1 gap-4 mb-6">
                    <div className="flex items-start p-4 bg-dark-700/50 rounded-lg border border-dark-600">
                      <div className="p-2 rounded-full bg-primary-900/30 text-primary-400 mr-3">
                        <FiUserCheck />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">Usuario Individual</h4>
                        <p className="text-gray-300 text-sm">Para profesionales, estudiantes y formadores del sector audiovisual.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start p-4 bg-dark-700/50 rounded-lg border border-dark-600">
                      <div className="p-2 rounded-full bg-primary-900/30 text-primary-400 mr-3">
                        <FiUserCheck />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">Empresa</h4>
                        <p className="text-gray-300 text-sm">Para proveedores de servicios, distribuidores y fabricantes del sector.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start p-4 bg-dark-700/50 rounded-lg border border-dark-600">
                      <div className="p-2 rounded-full bg-primary-900/30 text-primary-400 mr-3">
                        <FiUserCheck />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">Administrador</h4>
                        <p className="text-gray-300 text-sm">Para el personal designado de Avlink Network.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start p-4 bg-dark-700/50 rounded-lg border border-dark-600">
                      <div className="p-2 rounded-full bg-primary-900/30 text-primary-400 mr-3">
                        <FiUserCheck />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">Sponsor/Colaborador</h4>
                        <p className="text-gray-300 text-sm">Para empresas que desean patrocinar contenidos o eventos.</p>
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">3.3. Acceso a funcionalidades</h3>
                  <p className="mb-4 text-gray-300">Dependiendo del tipo de cuenta, los usuarios tendrán acceso a diferentes funcionalidades de la plataforma:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li><strong className="text-white">Usuarios Individuales:</strong> Acceso a recursos educativos, cursos, comunidad y ofertas laborales.</li>
                    <li><strong className="text-white">Empresas:</strong> Publicación de ofertas laborales, acceso a perfiles de profesionales y herramientas especializadas.</li>
                    <li><strong className="text-white">Sponsors/Colaboradores:</strong> Funcionalidades de visibilidad y participación en eventos y contenidos.</li>
                  </ul>
                </motion.section>
                
                {/* Sección 4: Derechos y Restricciones de Uso */}
                <motion.section 
                  id="section-4" 
                  ref={sectionRefs.section4}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">4</span>
                    <span>DERECHOS Y RESTRICCIONES DE USO</span>
                  </h2>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">4.1. Licencia limitada</h3>
                  <p className="mb-4 text-gray-300">Sujeto al cumplimiento de estos Términos, Avlink Network le otorga una licencia limitada, no exclusiva, no transferible y revocable para:</p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center p-3 bg-dark-700/50 rounded-lg">
                      <FiCheckCircle className="text-primary-500 mr-3 flex-shrink-0" />
                      <p className="text-gray-300">Acceder y ver el contenido disponible en la plataforma para su uso personal y profesional.</p>
                    </div>
                    <div className="flex items-center p-3 bg-dark-700/50 rounded-lg">
                      <FiCheckCircle className="text-primary-500 mr-3 flex-shrink-0" />
                      <p className="text-gray-300">Participar en cursos, eventos y otras actividades según su tipo de cuenta.</p>
                    </div>
                    <div className="flex items-center p-3 bg-dark-700/50 rounded-lg">
                      <FiCheckCircle className="text-primary-500 mr-3 flex-shrink-0" />
                      <p className="text-gray-300">Utilizar las herramientas y funcionalidades proporcionadas por la plataforma para los fines previstos.</p>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">4.2. Restricciones</h3>
                  <p className="mb-4 text-gray-300">Usted se compromete a no:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>Utilizar la plataforma de manera que viole cualquier ley o regulación aplicable.</li>
                    <li>Utilizar la plataforma para fines comerciales no autorizados.</li>
                    <li>Copiar, modificar, distribuir, vender o alquilar cualquier parte de la plataforma.</li>
                    <li>Realizar ingeniería inversa o intentar extraer el código fuente de la plataforma.</li>
                    <li>Eludir, desactivar o interferir con las características de seguridad de la plataforma.</li>
                    <li>Utilizar robots, spiders, scrapers u otros medios automatizados para acceder a la plataforma.</li>
                    <li>Interferir con o interrumpir la plataforma o los servidores o redes conectados a la plataforma.</li>
                    <li>Publicar contenido que sea ilegal, ofensivo, difamatorio, pornográfico o que viole derechos de terceros.</li>
                    <li>Suplantar la identidad de otra persona o entidad.</li>
                    <li>Compartir credenciales de acceso con terceros o permitir que utilicen su cuenta.</li>
                  </ul>
                </motion.section>
                
                {/* Sección 5: Contenido de la Plataforma */}
                <motion.section 
                  id="section-5" 
                  ref={sectionRefs.section5}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">5</span>
                    <span>CONTENIDO DE LA PLATAFORMA</span>
                  </h2>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">5.1. Propiedad intelectual</h3>
                  <p className="mb-6 text-gray-300">Todo el contenido disponible en la plataforma, incluyendo pero no limitado a textos, gráficos, logotipos, íconos, imágenes, clips de audio, descargas digitales, recopilaciones de datos y software, es propiedad de Avlink Network o de sus proveedores de contenido y está protegido por leyes de propiedad intelectual.</p>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">5.2. Contenido educativo</h3>
                  <p className="mb-4 text-gray-300">Los cursos, documentos técnicos y otros materiales educativos disponibles en la plataforma son para uso personal y profesional. No está permitido:</p>
                  <ul className="list-disc pl-5 space-y-2 mb-6 text-gray-300">
                    <li>Descargar, copiar o compartir el contenido con terceros fuera de la plataforma.</li>
                    <li>Utilizar el contenido para crear productos o servicios comerciales.</li>
                    <li>Modificar o crear trabajos derivados basados en el contenido.</li>
                    <li>Eliminar avisos de derechos de autor u otras notificaciones de propiedad.</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">5.3. Biblioteca técnica y recursos</h3>
                  <p className="mb-4 text-gray-300">Avlink Network ofrece una biblioteca técnica con documentación, manuales, estándares y guías del sector audiovisual. Esta documentación:</p>
                  <ul className="list-disc pl-5 space-y-2 mb-6 text-gray-300">
                    <li>Está disponible para consulta dentro de la plataforma.</li>
                    <li>Puede incluir contenido propio o proporcionado por terceros con los permisos correspondientes.</li>
                    <li>Está sujeta a las mismas restricciones de uso que el resto del contenido educativo.</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-white mb-4 mt-8 border-b border-dark-600 pb-2">5.4. Certificaciones</h3>
                  <p className="mb-4 text-gray-300">Las certificaciones emitidas por Avlink Network:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>Son personales e intransferibles.</li>
                    <li>No constituyen una acreditación o titulación oficial reconocida por autoridades educativas.</li>
                    <li>Representan la culminación exitosa de un curso o camino formativo específico dentro de la plataforma.</li>
                  </ul>
                </motion.section>
                
                {/* Sección de aceptación para pantallas pequeñas */}
                <div className="md:hidden mt-12 pt-6 border-t border-dark-600">
                  <h3 className="text-xl font-bold text-white mb-4">Aceptación de Términos y Condiciones</h3>
                  <p className="mb-4 text-gray-300">
                    Al marcar la casilla a continuación, confirma que ha leído, entendido y acepta estos Términos y Condiciones de uso de Avlink Network.
                  </p>
                  
                  <div className="flex items-center mb-6">
                    <input 
                      type="checkbox" 
                      id="acceptTerms" 
                      checked={accepted} 
                      onChange={() => setAccepted(!accepted)}
                      className="h-5 w-5 bg-dark-700 border-dark-600 rounded text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="acceptTerms" className="ml-2 text-gray-300">
                      He leído y acepto los Términos y Condiciones
                    </label>
                  </div>
                  
                  <div className="flex flex-col space-y-3">
                    <button 
                      onClick={handleAcceptTerms}
                      disabled={!accepted}
                      className={`py-3 px-6 rounded-lg flex items-center justify-center transition-colors ${
                        !accepted 
                          ? 'bg-dark-700 text-gray-500 cursor-not-allowed' 
                          : 'bg-primary-600 hover:bg-primary-500 text-white'
                      }`}
                    >
                      Aceptar Términos
                      <FiCheckCircle className="ml-2" />
                    </button>
                    
                    <button 
                      onClick={goToHome}
                      className="py-3 px-6 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors flex items-center justify-center"
                    >
                      <FiChevronLeft className="mr-2" />
                      Regresar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Final */}
            <motion.div 
              className="bg-gradient-to-r from-primary-900/50 to-dark-700 rounded-xl border border-dark-600 p-8 mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white mb-4">
                  ¿Listo para formar parte de la <span className="text-primary-400">comunidad Avlink</span>?
                </h2>
                <p className="text-gray-300 mb-6">
                  Únete a la red profesional líder en el sector audiovisual y aprovecha todos los 
                  recursos exclusivos para miembros.
                </p>
                
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link 
                    to="/register" 
                    className="py-3 px-8 bg-primary-600 hover:bg-primary-500 text-white font-bold rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center"
                  >
                    Crear cuenta
                    <FiArrowRight className="ml-2" />
                  </Link>
                  <Link 
                    to="/login" 
                    className="py-3 px-8 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg border border-primary-500/30 transition-all duration-300 flex items-center justify-center"
                  >
                    Iniciar sesión
                  </Link>
                </div>
              </div>
            </motion.div>
            
            <div className="text-center text-gray-500 text-sm">
              © 2025 Avlink Network. Todos los derechos reservados.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;